import ExcelJS from "exceljs";

/**
 * One employee's block on a category's "… Timesheet" sheet: the individual
 * timesheet rows the category total was summed from.
 *
 * The header is styled like the category summary sheet and the body like the
 * timesheet export it is taken from, minus the per-day total column.
 *
 * Deliberately free of app imports (no i18n, no stores) so the sheet can be
 * generated and inspected outside the browser.
 */

export interface ICategoryTimesheetTask {
	/** ISO `YYYY-MM-DD`, as the API returns it */
	date: string
	project_name: string
	project_description: string
	category: string
	task: string
	time: string
	hours: number
}

export interface ICategoryTimesheetUser {
	name: string
	tasks: ICategoryTimesheetTask[]
}

export interface ICategoryTimesheetLabels {
	date: string
	project: string
	project_name: string
	category: string
	task: string
	time: string
	hours: string
	total: string
}

const FONT_NAME = "Calibri";
const ACCENT_COLOR = "FFEF3129";
const BLACK = { argb: "FF000000" };

// Fixed layout: # B, Date C, Project D, Project Name E, Category F,
// Task G, Time H, hours I.
const FIRST_COLUMN = 2;
const DATE_LETTER = "C";
const HOURS_LETTER = "I";
const LAST_COLUMN = FIRST_COLUMN + 7;

const round = (value: number) => Math.round(value * 10) / 10;

export const categoryTimesheetColumnWidths: { [letter: string]: number } = {
	B: 5, C: 12, D: 13.5, E: 23, F: 17.5, G: 46, H: 12.8, I: 7,
};

/**
 * Writes one employee's block and returns the row the next block starts on.
 */
export function createCategoryTimesheetTable(
	worksheet: ExcelJS.Worksheet,
	user: ICategoryTimesheetUser,
	startRow: number,
	labels: ICategoryTimesheetLabels,
	translateCategory: (slug: string) => string,
): number {

	const hairBorder: Partial<ExcelJS.Borders> = {
		top: { style: "hair", color: BLACK },
		left: { style: "hair", color: BLACK },
		bottom: { style: "hair", color: BLACK },
		right: { style: "hair", color: BLACK },
	};

	// Employee name, boxed across the full width of the table so the block reads
	// as one thing rather than trailing off mid-row
	worksheet.mergeCells([startRow, FIRST_COLUMN, startRow, LAST_COLUMN]);
	const nameCell = worksheet.getCell(startRow, FIRST_COLUMN);
	nameCell.value = user.name;
	nameCell.font = { name: FONT_NAME, size: 10, family: 0, bold: true };
	nameCell.alignment = { horizontal: "left", vertical: "middle" };
	nameCell.border = {
		top: { style: "thin", color: BLACK },
		left: { style: "thin", color: BLACK },
		bottom: { style: "thin", color: BLACK },
		right: { style: "thin", color: BLACK },
	};

	// Column headings
	const headerRow = startRow + 1;
	const titles = ["#", labels.date, labels.project, labels.project_name,
		labels.category, labels.task, labels.time, labels.hours];

	titles.forEach((title, index) => {
		const cell = worksheet.getCell(headerRow, FIRST_COLUMN + index);
		cell.value = title;
		cell.font = { name: FONT_NAME, size: 11, color: { argb: "FFFFFFFF" }, bold: true };
		cell.alignment = { horizontal: "center", vertical: "middle" };
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ACCENT_COLOR } };
		cell.border = {
			left: { style: "thin", color: BLACK },
			bottom: { style: "thin", color: BLACK },
			right: { style: "thin", color: BLACK },
		};
	});

	// Tasks
	const bodyStart = headerRow + 1;
	let totalValue = 0;

	user.tasks.forEach((task, index) => {
		const row = bodyStart + index;
		const [year, month, day] = task.date.split("-").map(Number);

		const values: (string | number | Date)[] = [
			index + 1,
			// UTC midnight, not local: ExcelJS turns a Date into a serial number
			// through UTC, so a local midnight east of Greenwich lands on the
			// previous day in the sheet.
			new Date(Date.UTC(year, month - 1, day)),
			task.project_name,
			task.project_description,
			task.category ? translateCategory(task.category) : "-",
			task.task,
			task.time,
			round(task.hours),
		];

		values.forEach((value, column) => {
			const cell = worksheet.getCell(row, FIRST_COLUMN + column);
			cell.value = value;
			cell.font = { name: FONT_NAME, size: 10, family: 0 };
			cell.border = hairBorder;
			cell.alignment = column === 5
				? { horizontal: "left", vertical: "top", wrapText: true }
				: { horizontal: "center", vertical: "middle" };
		});

		worksheet.getCell(row, FIRST_COLUMN + 1).numFmt = "dd/mm/yy";
		worksheet.getCell(row, LAST_COLUMN).numFmt = "0.0";

		totalValue += task.hours;
	});

	// A day is written once and spans its tasks, as in the timesheet. The rows
	// arrive sorted by date, so equal dates are always neighbours.
	let cursor = 0;
	while (cursor < user.tasks.length) {
		let span = 1;
		while (cursor + span < user.tasks.length
			&& user.tasks[cursor + span].date === user.tasks[cursor].date) span++;
		if (span > 1) {
			worksheet.mergeCells(`${DATE_LETTER}${bodyStart + cursor}:${DATE_LETTER}${bodyStart + cursor + span - 1}`);
		}
		cursor += span;
	}

	// Total. An employee with no tasks still gets the row, so the block reads
	// the same whether or not anything was booked.
	const endRow = bodyStart + Math.max(user.tasks.length, 1);

	worksheet.mergeCells([endRow, FIRST_COLUMN, endRow, LAST_COLUMN - 1]);
	const totalLabel = worksheet.getCell(endRow, FIRST_COLUMN);
	totalLabel.value = labels.total;
	totalLabel.font = { name: FONT_NAME, size: 10, family: 0, bold: true };
	totalLabel.alignment = { horizontal: "right", vertical: "middle" };
	totalLabel.border = {
		top: { style: "thin", color: BLACK },
		left: { style: "thin", color: BLACK },
		bottom: { style: "thin", color: BLACK },
	};

	const totalCell = worksheet.getCell(endRow, LAST_COLUMN);
	totalCell.value = user.tasks.length
		? { formula: `SUM(${HOURS_LETTER}${bodyStart}:${HOURS_LETTER}${endRow - 1})`, result: round(totalValue), date1904: false }
		: 0;
	totalCell.numFmt = "0.0";
	totalCell.font = { name: FONT_NAME, size: 10, family: 0, bold: true };
	totalCell.alignment = { horizontal: "center", vertical: "middle" };
	totalCell.border = {
		top: { style: "thin", color: BLACK },
		bottom: { style: "thin", color: BLACK },
		right: { style: "thin", color: BLACK },
	};

	// Two blank rows before the next employee, as in the agreed layout
	return endRow + 3;
}
